'use strict';

var defaults = require('../defaults/Defaults');
var FirebaseObject = require('./FirebaseObject');
var KayleeBase = require('./KayleeBase');
var KayleeCache = require('./KayleeCache');
var KayleeDirectories = require('./KayleeDirectories');
var KayleeEvent = require('./KayleeEvent');
var KayleeEvents = require('./KayleeEvents');
var KayleeOffice365 = require('./KayleeOffice365');
var KayleeSite = require('./KayleeSite');
var KayleeSites = require('./KayleeSites');
var KayleeUsers = require('./KayleeUsers');
var KayleeUtil = require('./KayleeUtil');
var log = require('../log/Log').channel('KayleeCompany');
var sutl = require('../sutl/Sutl');

var KayleeCompany = {
    addCompany: addCompany,
    fromJson: fromJson,
    getCompany: getCompany,
};

function addUpdatesToCreateCompany(update, companyId, name, userId) {
    if (typeof userId === 'undefined' || userId === null) {
        if (KayleeBase.app.auth().currentUser === null) return null;
        userId = KayleeBase.app.auth().currentUser.uid;
    }

    update['companies/' + companyId] = { name: name };
    update['users/' + userId + '/roles/companies/' + companyId + '/role'] = 'admin';
    update['index/usersByCompany/' + companyId + '/' + userId] = true;

    return KayleeEvent.addUpdatesForCompanyCreatedEvent(update, companyId, name)
    .then(function(update) {
        var parentEventId = KayleeEvent.getEventIdFromUpdates(update);
        return KayleeEvent.addUpdatesForRoleCreatedEventOnNewNode(
            update, userId, 'admin', parentEventId, companyId, name);
    });
}

function addUpdatesToRemoveCompany(update, companyId) {
    var parentEventId;
    update['companies/' + companyId] = null;
    update['index/usersByCompany/' + companyId] = null;

    return KayleeEvent.addUpdatesForCompanyDeletedEvent(update, companyId)
    .then(function() {
        parentEventId = KayleeEvent.getEventIdFromUpdates(update);

        if (parentEventId === null) {
            return Promise.reject(new Error('couldn\'t get event id'));
        }

        return KayleeUtil.loadUsersByCompanyIndex({companyId: companyId});
    }).then(function(obj) {
        // Remove organization billing account, if it exists.

        var billingAccountRef =
            KayleeBase.app.database().ref('organizationBillingAccounts/' + companyId);
        return billingAccountRef.get()
        .then(function(snap) {
            if (snap.exists()) {
                return KayleeBase.request('chargebeeCustomerDelete',
                    { data: { organizationId: companyId } });
            } else {
                return;
            }
        })
        .then(function() {
            return obj;
        });
    }).then(function(obj) {
        // Remove company roles for any users who have them.
        var userRolePromises = [];
        var removeUserRole = function(obj) {
            if (sutl.nodeExists(obj.user,
                ['roles', 'companies', companyId, 'role'])) {
                update['users/' + obj.userId + '/roles/companies/' +
                    companyId + '/role'] = null;
                return KayleeEvent.addUpdatesForRoleDeletedEvent(update,
                    obj.userId, obj.user.roles.companies[companyId].role,
                    companyId, null, null,
                    KayleeBase.app.auth().currentUser ? KayleeBase.app.auth().currentUser.uid : null,
                    parentEventId);
            }

            return update;
        };

        for (var userId in obj.usersByCompany) {
            if (!obj.usersByCompany.hasOwnProperty(userId)) continue;

            userRolePromises.push(
                KayleeUtil.loadUserData({userId: userId})
                .then(removeUserRole));
        }

        return Promise.all(userRolePromises);
    }).then(function() {
        // Remove any sites belonging to this company. The children rooms,
        // equipment, and controllers will be handled as well.

        return KayleeUtil.loadSitesByCompanyIndex({companyId: companyId}, false)
        .then(function(obj) {
            var sitePromises = [];

            for (var siteId in obj.sitesByCompany) {
                if (!obj.sitesByCompany.hasOwnProperty(siteId)) continue;
                sitePromises.push(KayleeSite.addUpdatesToRemoveSite(
                    update, companyId, siteId, parentEventId));
            }

            return Promise.all(sitePromises);
        });
    }).then(function() {
        return update;
    });
}

// Returns a promise that resolves to the id of the company that was added. The
// specified user is granted an admin role on the new company. If no user is
// specified, then the current user is used.

function addCompany(name, userId) {
    var companyId = KayleeBase.app.database().ref().push().key;
    var update = {};

    return addUpdatesToCreateCompany(update, companyId, name, userId)
    .then(function(update) {
        return KayleeBase.update(update);
    })
    .then(function() {
        log.info('added [Company "' + name + '" (' + companyId + ')]');
        return Promise.resolve(companyId);
    });
}

function fromJson(data) {
    var reqProps = [ '$id' ];
    return FirebaseObject.$fromJson(data, 'Company', reqProps, reqProps,
        function(data) { return getCompany(data.$id); }
    );
}

function getCompany(companyId) {
    var key = 'Company: ' + companyId;
    var company = KayleeCache.get(key);

    if (company === null) {
        company = new Company(companyId);
        KayleeCache.put(key, company);
    }

    return company;
}

function Company(companyId) {
    this.$nodeType = 'company';
    this.$sites = null;
    FirebaseObject.call(this,
        KayleeBase.app.database().ref('companies/' + companyId));
}

Company.prototype = Object.create(FirebaseObject.prototype);
Company.prototype.constructor = Company;

Company.prototype.$defaults = {
    settings: {
        timeZone: defaults.inara.timeZone,
    },
};

Company.prototype.$includeInChangedEvent = {
    name: true,
    settings: true,
};

Company.prototype.$addUpdatesForChangedEvent =
    KayleeEvent.addUpdatesForCompanyChangedEvent;

Company.prototype.$toJson = function() {
    return FirebaseObject.prototype.$toJson.call(this, [ '$id' ]);
};

Company.prototype.toString = function() {
    if (this.$internalIsLoaded) {
        return '[Company "' + this.name  + '" (' + this.$id + ')]';
    }

    return '[Company (' + this.$id + ')]';
};

// Returns a promise that resolves when the company is removed.

Company.prototype.$remove = function() {
    var self = this;
    var update = {};

    if (!self.$internalIsLoaded) {
        var err = new Error(self + ' not loaded');
        log.error(err.message);
        return Promise.reject(err);
    }

    return addUpdatesToRemoveCompany(update, self.$id)
    .then(function(update) {
        return KayleeBase.update(update);
    })
    .then(function() {
        log.info('removed ' + self);
    });
};

Company.prototype.$getSites = function(userId) {
    return KayleeSites.getSites(this, userId);
};

Company.prototype.$addSite = function(name, address) {
    var siteId = this.$ref.push().key;
    var update = {};

    return KayleeSite.addUpdatesToCreateSite(update, this.$id, siteId, name, address)
    .then(function(update) {
        return KayleeBase.update(update);
    })
    .then(function() {
        log.info('added [Site "' + name + '" (' + siteId + ')]');
        return siteId;
    });
};

Company.prototype.$getUsers = function() {
    return KayleeUsers.getUsers(this);
};

Company.prototype.$getEvents = function(limit) {
    return KayleeEvents.getEvents(this, limit);
};

Company.prototype.$getAlertCount = function() {
    if (!this.$internalIsLoaded) return 0;

    if (this.$sites === null) {
        this.$sites = this.$getSites();
    }

    return this.$sites.$getAlertCount();
};

Company.prototype.$getControllerCount = function() {
    if (!this.$internalIsLoaded) return 0;

    if (this.$sites === null) {
        this.$sites = this.$getSites();
    }

    return this.$sites.$getControllerCount();
};

Company.prototype.$getInCallCount = function() {
    if (!this.$internalIsLoaded) return 0;

    if (this.$sites === null) {
        this.$sites = this.$getSites();
    }

    return this.$sites.$getInCallCount();
};

Company.prototype.$getRoomCount = function() {
    if (!this.$internalIsLoaded) return 0;

    if (this.$sites === null) {
        this.$sites = this.$getSites();
    }

    return this.$sites.$getRoomCount();
};

Company.prototype.$enableOffice365 = function(domain) {
    return KayleeBase.request('office365AuthorizationUriRequest', {
        companyId: this.$id,
        domain: domain,
    });
};

Company.prototype.$disableOffice365 = function() {
    return KayleeBase.request('office365DisableCompanyRequest', {
        companyId: this.$id
    });
};

Company.prototype.$getOffice365Status = function() {
    return KayleeOffice365.getAuthorizationStatus(this);
};

Company.prototype.$getDirectories = function() {
    return KayleeDirectories.getDirectories(this);
};

module.exports = KayleeCompany;
